import type { ResourceAccessRequestRecipient } from "@eai-toolkit/client";

import { parseAccountFromUrn } from "@helpers/utils";

import type { ResourceType } from "../../../../store/api/resourceTypes";

export type TargetType = "role" | "user" | "team";

export const EXPIRATION_OPTIONS = [
  { value: 30, label: "30 days" },
  { value: 60, label: "60 days" },
  { value: 90, label: "90 days" },
  { value: null, label: "Permanent access" },
] as const;

export type SelectedResource = {
  id: string;
  name: string;
  type: ResourceType;
  account?: string;
  organization?: string;
  urn?: string;
};

export type SelectedTarget = {
  id: string;
  name: string;
};

export type AccessRequestFormState = {
  // Null means not selected yet - except for expirationDays where it means permanent access
  resource: SelectedResource | null;
  resourceType: ResourceType;
  action: string | null;
  target: SelectedTarget | null;
  targetType: TargetType;
  shareable: boolean;
  expirationDays: number | null;
  scope: SelectedResource | null;
  requestReason: string;
};

export const INITIAL_FORM_STATE: AccessRequestFormState = {
  resource: null,
  resourceType: "data",
  action: null,
  target: null,
  targetType: "user",
  shareable: false,
  expirationDays: EXPIRATION_OPTIONS[0].value,
  scope: null,
  requestReason: "",
};

export const MIN_REASON_LENGTH = 20;

export function validateRequestReason(reason: string): string | null {
  if (!reason || reason.trim().length === 0) {
    return "Request reason is required";
  }
  if (reason.trim().length < MIN_REASON_LENGTH) {
    return `Request reason must be at least ${MIN_REASON_LENGTH} characters`;
  }
  return null;
}

export function isFormValid(state: AccessRequestFormState): boolean {
  return (
    state.resource !== null &&
    state.action !== null &&
    state.target !== null &&
    (!state.shareable || state.scope !== null) &&
    !validateRequestReason(state.requestReason)
  );
}

export function getExpirationDate(days: number | null): string | null {
  if (days === null) {
    return null; // Permanent access
  }
  const now = new Date();
  now.setDate(now.getDate() + days);
  return now.toISOString();
}

export function getDefaultScope(
  resource: SelectedResource | null,
  resolveAccount?: (accountRef: string) => { id: string; name: string },
): SelectedResource | null {
  if (!resource) {
    return null;
  }
  if (resource.type === "account") {
    return {
      id: resource.id,
      name: resource.name,
      type: "account",
    };
  }
  // || instead of ?? to make sure we don't use an empty string account
  const account = resource.account || parseAccountFromUrn(resource.urn);
  if (account) {
    const resolved = resolveAccount?.(account);
    return {
      id: resolved?.id ?? account,
      name: resolved?.name ?? account,
      type: "account",
    };
  }
  return null;
}

export type LocalAccessRequest = ResourceAccessRequestRecipient & {
  id: string;
};

export function toLocalAccessRequest(
  resourceAccessRequest: ResourceAccessRequestRecipient,
): LocalAccessRequest {
  return {
    ...resourceAccessRequest,
    id: resourceAccessRequest.requestId ?? "",
  };
}
